import { CurrencyPipe, DOCUMENT } from '@angular/common';
import { Directive, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { DomSanitizer, Meta, Title } from '@angular/platform-browser';
import {
  QbItaCapitalizeFirstPipe,
  QbItaParameterResult,
  QbItaSignCurrencyPipe,
  Vehicle,
} from '@fwmovilidad/qb-home-ita-components-lib';
import { Flow, RoutePaths } from '@global/routePaths';
import { select, Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LoadingService } from '@services/loading/loading.service';
import { AppState } from '@store/app.state';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { delay, filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { newQuotationSteps } from '@pages/flow-routes/new-quotation/new-quotation.steps';
import { ActivationEnd, Navigation, Router } from '@angular/router';
import cloneDeep from 'clone-deep';
import { ModalController, ModalOptions } from '@ionic/angular';
import { GeneralService } from '@core/services/api/http/general.service';
import { GenericErrorModalService } from '@core/services/generic-error/generic-error.service';
import { QuestionEnum, TariffTypeEnum, UrlParams } from '@global/enums';
import { DeviceDetectorService } from 'ngx-device-detector';
import { NONE, PARAMS_WHITELIST } from '@global/constants';
import { getQuote } from '@store/flow-data/quote';
import { InitFlow, ProductLine, Quote } from '@core/models/swagger';
import { getInitFlow } from '@store/flow-data/init-flow';
import { InitFlowService } from '@core/services/api/http/initFlow.service';
import { QueryParamsStore } from '@core/services/queryparams-store/queryparams-store.service';
import { ValidationService } from '@core/services/api/http/validation.service';
import { CatalogService } from '@core/services/api/http/catalog.service';
import {
  getFullOperationId,
  getPartnerSearch,
  getPrevUrl,
  getProductLines,
  getProgressId,
  getQuotingId,
  getSelectedProductLine,
  UpdateFullOperationId,
  UpdatePrevUrl,
  UpdateProgressId,
  UpdateQuotingId,
} from '@store/flow-data/general';
import { AnalyticsService } from '@core/services/analytics/analytics.service';
import { MultiQuoteResponse, SearchPartnerResponse } from '@core/models/swagger/bff';
import { StepResolverService } from '@core/services/analytics/step-resolver.service';
import { environment } from '@environments/environment';

@Directive()
export abstract class FlowAbstractPage implements OnInit, OnDestroy {
  protected steps: unknown;
  protected abstract previousRoute: string;
  protected abstract nextRoute: string;
  protected abstract analyticStepName: string;
  protected abstract analyticFlowName: string;
  protected abstract analyticStepNumber: Record<string, string>;

  alreadyVisited = false;

  protected readonly store: Store<AppState>;
  protected readonly router: Router;
  protected readonly formBuilder: FormBuilder;
  private readonly modalCtrl: ModalController;
  protected readonly loadingService: LoadingService;
  protected readonly titleService: Title;
  protected readonly translateService: TranslateService;
  protected readonly generalService: GeneralService;
  protected readonly validationService: ValidationService;
  protected readonly catalogService: CatalogService;
  protected readonly sanitizer: DomSanitizer;
  protected readonly metaService: Meta;
  protected readonly genericErrorModalService: GenericErrorModalService;
  protected readonly initFlowService: InitFlowService;
  protected readonly analyticsService: AnalyticsService;
  protected readonly stepResolverService: StepResolverService;
  protected readonly deviceDetectorService: DeviceDetectorService;
  protected readonly queryParamsStore: QueryParamsStore;
  readonly generalLoading$: Observable<boolean>;

  protected destroy$: Subject<void> = new Subject<void>();
  protected currencyPipe: CurrencyPipe;
  protected signCurrencyPipe: QbItaSignCurrencyPipe;
  protected translatePipe: TranslatePipe;
  protected capitalizeFirstPipe: QbItaCapitalizeFirstPipe;
  readonly FLOW: Flow;
  readonly PROJECT_PAST_EFFECTIVE_DATE_IND: boolean;

  readonly DELAY$ = of(true).pipe(
    delay(500),
    map(() => false)
  );

  readonly NEW_QUOTATION: Flow = Flow.NEW_QUOTATION;

  quote: Quote;
  initFlowData: InitFlow;
  prevUrl: string;

  currentNavigationData: Navigation;

  stepNumber: string;
  stepDescription: string;
  stepCommon: string;
  stepBasic: string;

  isModalOpen: boolean = false;

  constructor() {
    this.store = inject(Store);
    this.router = inject(Router);
    this.formBuilder = inject(FormBuilder);
    this.modalCtrl = inject(ModalController);
    this.translatePipe = inject(TranslatePipe);
    this.capitalizeFirstPipe = inject(QbItaCapitalizeFirstPipe);
    this.loadingService = inject(LoadingService);
    this.titleService = inject(Title);
    this.translateService = inject(TranslateService);
    this.generalService = inject(GeneralService);
    this.validationService = inject(ValidationService);
    this.catalogService = inject(CatalogService);
    this.stepResolverService = inject(StepResolverService);
    this.sanitizer = inject(DomSanitizer);
    this.metaService = inject(Meta);
    this.genericErrorModalService = inject(GenericErrorModalService);
    this.initFlowService = inject(InitFlowService);
    this.analyticsService = inject(AnalyticsService);
    this.deviceDetectorService = inject(DeviceDetectorService);
    this.queryParamsStore = inject(QueryParamsStore);

    this.currentNavigationData = this.router.getCurrentNavigation();
    this.FLOW = this.currentNavigationData?.extras?.state?.flow ?? this.NEW_QUOTATION;
    this.setMetaTags();

    this.generalLoading$ = combineLatest([this.generalService.postMultiQuoteLoading$]).pipe(
      map(loadings => loadings.some(Boolean))
    );
  }

  ngOnInit(): void {
    this.store
      .pipe(select(getQuote))
      .pipe(takeUntil(this.destroy$))
      .subscribe((quote: Quote) => {
        this.quote = quote;
      });

    this.store
      .pipe(select(getSelectedProductLine))
      .pipe(takeUntil(this.destroy$))
      .subscribe((productLine: ProductLine) => {
        if (environment.environmentName !== 'PRO'){
          console.log(productLine);
        }
      });

    this.store.pipe(select(getInitFlow), takeUntil(this.destroy$)).subscribe((initFlowData: InitFlow) => {
      this.initFlowData = initFlowData;
    });

    this.store.pipe(select(getPrevUrl), takeUntil(this.destroy$)).subscribe((prevUrl: string) => {
      this.prevUrl = prevUrl;
    });

    this.initializeSteps();
  }

  private initializeSteps(): void {
    switch (this.FLOW) {
      case this.NEW_QUOTATION:
        this.steps = newQuotationSteps;
        break;
      default:
        break;
    }
  }

  protected abstract initForm(): void;

  protected abstract saveStepData(): void;

  setPageAnalytics(): void {}

  goNextStep(payload?: Record<string, string>): void {
    this.navigateTo(this.nextRoute, {
      ...payload,
    });
  }

  goBackStep(payload?: Record<string, string>): void {
    this.navigateTo(this.previousRoute, {
      ...payload,
    });
  }

  goToStep(route: string, payload?: Record<string, string>): void {
    this.navigateTo(route, { ...payload });
  }

  protected navigateTo(route: string, payload?: Record<string, unknown>): void {
    const allowedKeys = PARAMS_WHITELIST.map(key => UrlParams[key as keyof typeof UrlParams]);

    const currentParams = this.currentNavigationData?.extractedUrl?.queryParams;
    const filteredParams: Record<string, unknown> = {};

    for (const key of allowedKeys) {
      if (currentParams?.[key] !== undefined) {
        filteredParams[key] = currentParams[key];
      }
    }

    this.store.dispatch(UpdatePrevUrl({ data: this.router.url }));
    this.router.navigate([route], {
      queryParams: filteredParams,
      // queryParamsHandling: 'merge',
      state: {
        flow: this.FLOW,
        ...payload,
        allowNavigation: true,
      },
    });
  }

  protected cloneDeep<T>(value: T): T {
    // return window.structuredClone(value);
    return cloneDeep(value);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  async openModal(id: string, options: ModalOptions): Promise<HTMLIonModalElement> {
    const componentProps: ModalOptions = {
      ...options,
      component: options.component,
      componentProps: {
        ...options.componentProps,
        id,
        isModal: true,
        backdropDismiss: false,
      },
      id,
    };
    const modal = await this.modalCtrl.create(componentProps);
    await modal.present();
    this.isModalOpen = true;
    return modal;
  }

  dismissModal(): void {
    this.isModalOpen = false;
  }

  getUrlLastSegment(path: string): string {
    const segments = path.split('/');
    return segments[segments.length - 1];
  }

  goToErrorPage(): void {
    this.navigateTo(RoutePaths.GENERIC_ERROR.route, { continueToErrorPage: true });
  }

  private setMetaTags(): void {
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter(event => event instanceof ActivationEnd),
        tap((route: ActivationEnd) => {
          if (route?.snapshot?.routeConfig?.path === RoutePaths.NEW_QUOTATION.SITUATION.VERTI_CLIENT.route) {
            this.metaService.updateTag({ name: 'robots', content: 'index, follow' });
          } else {
            this.metaService.updateTag({ name: 'robots', content: 'noindex, follow' });
          }
        })
      )
      .subscribe();
  }

  private updateAnalyticsDigitalData(): void {
    combineLatest([
      this.store.select(getPartnerSearch),
      this.store.select(getSelectedProductLine),
      this.store.select(getQuote),
      this.store.select(getInitFlow),
      this.store.select(getProgressId),
      this.store.select(getFullOperationId),
      this.store.select(getQuotingId),
      this.store.select(getProductLines),
    ])
      .pipe(take(1), delay(200))
      .subscribe(
        ([partnerSearch, productLine, quote, initflow, progressId, fullOperationId, quotingId, productLines]: [
          SearchPartnerResponse | null,
          ProductLine | null,
          Quote | null,
          InitFlow | null,
          number,
          string | null,
          string | null,
          MultiQuoteResponse | null,
        ]) => {
          const updatedProgressId = ++progressId;
          this.store.dispatch(UpdateProgressId({ data: updatedProgressId }));
          const fullOperationIdAnalytic = fullOperationId ?? crypto.randomUUID();
          if (!fullOperationId) {
            this.store.dispatch(UpdateFullOperationId({ data: fullOperationIdAnalytic }));
          }

          const quotingIdAnalytic = quotingId ?? crypto.randomUUID();
          if (!quotingId) {
            this.store.dispatch(UpdateQuotingId({ data: quotingIdAnalytic }));
          }

          /**
           * User
           */
          this.analyticsService.setEmail(quote.policyholderData);
          this.analyticsService.setUserPhone(quote.policyholderData);
          this.analyticsService.setFirstName(quote.policyholderData);
          this.analyticsService.setLastName(quote.policyholderData);
          this.analyticsService.setGender(quote.policyholderData);
          this.analyticsService.setStreet(quote.policyholderData);
          this.analyticsService.setHouseNumber(quote.policyholderData);
          this.analyticsService.setCity(quote.policyholderData);
          this.analyticsService.setUserZipCode(quote.policyholderData);

          /**
           * Error
           */
          this.analyticsService.setErrorType('');
          this.analyticsService.setErrorMessage('');

          /**
           * Process
           */
          this.analyticsService.setFullOperationId(fullOperationIdAnalytic);
          this.analyticsService.setFlowId(this.FLOW);
          this.analyticsService.setStepNumber(this.stepNumber);
          this.analyticsService.setStepDescription(this.stepDescription);
          this.analyticsService.setStepCommon(this.stepNumber);
          this.analyticsService.setStepBasic(this.stepBasic);
          this.analyticsService.setProgressId(updatedProgressId.toString());
          this.analyticsService.setPurchasePrice(productLine?.price?.default);
          this.analyticsService.setModalityProcess(productLine?.productLine.value);

          /**
           * Custom
           */
          this.analyticsService.setInsuranceStartDate(quote?.insuranceData?.insuranceBeginDate);
          this.analyticsService.setZipCode(quote?.policyholderData?.address?.postCode);
          this.analyticsService.setPolicyholderType(quote?.insuranceData?.insuranceType);
          this.analyticsService.setPolicyholderBirthday(quote?.policyholderData?.birthdate);
          this.analyticsService.setCustomerId(quote?.policyholderData?.contact?.email);
          this.analyticsService.setPreviousInsuranceBrand(quote?.previousInsuranceData?.insurer?.parameterId, []);
          this.analyticsService.setPromotionalCode(quote?.insuranceData?.promotionCode);
          this.analyticsService.setInsuranceModalitiesOffered(initflow);
          this.analyticsService.setInsurancePricesOffered(productLines);
          this.analyticsService.setTransactionId(quote.insuranceData?.contractNumber);
          this.analyticsService.setQuotingId(quotingIdAnalytic);
          this.analyticsService.setClaimReport365Days(
            quote?.previousInsuranceData?.previousInsurance === undefined
              ? undefined
              : quote?.previousInsuranceData?.previousInsurance === false
                ? 'None'
                : quote?.previousInsuranceData?.claims?.parameterId !== 'None'
                  ? 'Ja'
                  : 'none'
          );
          this.analyticsService.setPreviousInsuranceDuration(undefined);
          this.analyticsService.setProfession(quote?.policyholderData?.occupation?.parameterId);
          this.analyticsService.setPhone(quote?.policyholderData?.contact?.phone);
          this.analyticsService.setPaymentMethod(quote?.bankAccountOwnerData?.paymentMethod);
          this.analyticsService.setCustomerStatus(
            quote?.isVertiClient === undefined ? undefined : quote?.isVertiClient ? true : false
          );
          this.analyticsService.setPrevInsurerCanceled(
            quote?.previousInsuranceData?.canceller?.parameterId === undefined
              ? undefined
              : quote?.previousInsuranceData?.canceller?.parameterId === 'insurer'
                ? true
                : false
          );
          this.analyticsService.setTestUser(this.queryParamsStore.snapshot().test === 'true');
          this.analyticsService.sendStepView(this.analyticStepName);
        }
      );
  }

  sendStepView({
    stepName = this.analyticStepName,
  }: Partial<{
    stepName: string;
    flow: string;
    stepNumber: string;
  }> = {}): void {
    this.updateAnalyticsDigitalData();
    // this.updateActivityRegistryStepData();
  }

  // private updateActivityRegistryStepData(): void {
  //   this.store.dispatch(
  //     UpdateStepInformation({
  //       data: {
  //         stepBasic: this.stepBasic,
  //         stepCommon: this.stepCommon,
  //         stepDescription: this.stepDescription,
  //         stepNumber: this.stepNumber,
  //         flowName: this.FLOW,
  //         componentName: '',
  //         isModalOpen: this.isModalOpen,
  //       },
  //     })
  //   );
  // }
}
